import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import { Box, Checkbox, ListItemText, Menu, MenuItem, TextField } from "@mui/material";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ALL_OPTION_ID,
  OTHER_OPTION_ID,
  optionHintReasons,
  realSlotOptions,
  renderSlotSelection,
} from "@/features/chat/lib/clarificationRewrite";
import type {
  ClarificationSlot,
  ClarificationSlotOption,
} from "@/features/chat/model/chatTypes";

export type ClarificationSlotDropdownProps = {
  slot: ClarificationSlot;
  selectedOptionIds: string[];
  customText: string;
  disabled?: boolean;
  onSelectionChange: (optionIds: string[]) => void;
  onCustomTextChange: (value: string) => void;
};

export function ClarificationSlotDropdown({
  slot,
  selectedOptionIds,
  customText,
  disabled = false,
  onSelectionChange,
  onCustomTextChange,
}: ClarificationSlotDropdownProps) {
  const { t } = useTranslation("chat");
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const customTextRef = useRef<HTMLInputElement | null>(null);

  const candidates = useMemo(() => realSlotOptions(slot), [slot]);
  const allowMultiple = slot.allow_multiple !== false && candidates.length > 1;
  const isOther = selectedOptionIds.includes(OTHER_OPTION_ID);
  const isAll = selectedOptionIds.includes(ALL_OPTION_ID);
  const triggerLabel = renderSlotSelection(slot, selectedOptionIds, customText);

  const closeMenu = () => setAnchorEl(null);

  const handleCandidateClick = (optionId: string) => {
    if (!allowMultiple || isAll || isOther) {
      onSelectionChange([optionId]);
      closeMenu();
      return;
    }
    // Multi-select: toggle, but never leave the span with nothing to render.
    const next = selectedOptionIds.includes(optionId)
      ? selectedOptionIds.filter((id) => id !== optionId)
      : [...selectedOptionIds, optionId];
    onSelectionChange(next.length > 0 ? next : selectedOptionIds);
  };

  const handleSyntheticClick = (optionId: string) => {
    onSelectionChange([optionId]);
    closeMenu();
    if (optionId === OTHER_OPTION_ID) {
      // Defer so the field exists once the menu has closed.
      window.setTimeout(() => customTextRef.current?.focus(), 0);
    }
  };

  const optionHint = (option: ClarificationSlotOption): string | undefined => {
    const reasons = optionHintReasons(option);
    return reasons.length > 0
      ? reasons.map((reason) => t(`clarification.optionHint.${reason}`)).join(" · ")
      : undefined;
  };

  const renderOptionRow = (
    optionId: string,
    label: string,
    checked: boolean,
    hint?: string,
  ) => (
    <MenuItem
      key={optionId}
      onClick={() =>
        optionId === ALL_OPTION_ID || optionId === OTHER_OPTION_ID
          ? handleSyntheticClick(optionId)
          : handleCandidateClick(optionId)
      }
      selected={checked}
      data-testid={`clarification-slot-option-${slot.id}-${optionId}`}
      sx={{ color: "var(--theme-text-primary)" }}
    >
      {allowMultiple && optionId !== ALL_OPTION_ID && optionId !== OTHER_OPTION_ID ? (
        <Checkbox
          size="small"
          checked={checked}
          tabIndex={-1}
          disableRipple
          sx={{ mr: 0.5, p: 0.5, color: "var(--theme-text-secondary)" }}
        />
      ) : null}
      <ListItemText
        primary={label}
        secondary={hint}
        slotProps={{
          primary: { variant: "body2" },
          secondary: { variant: "caption", sx: { color: "var(--theme-text-secondary)" } },
        }}
      />
    </MenuItem>
  );

  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "baseline" }}>
      {isOther ? (
        <TextField
          inputRef={customTextRef}
          variant="standard"
          size="small"
          value={customText}
          disabled={disabled}
          placeholder={t("clarification.otherPlaceholder")}
          onChange={(event) => onCustomTextChange(event.target.value)}
          slotProps={{
            htmlInput: { "aria-label": t("clarification.slotAria", { term: slot.term }) },
          }}
          data-testid={`clarification-slot-custom-${slot.id}`}
          sx={{
            minWidth: 160,
            "& .MuiInput-input": { color: "var(--theme-text-primary)", py: 0 },
          }}
        />
      ) : null}
      <Box
        component="button"
        type="button"
        disabled={disabled}
        aria-haspopup="true"
        aria-expanded={anchorEl !== null}
        aria-label={t("clarification.slotAria", { term: slot.term })}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        data-testid={`clarification-slot-trigger-${slot.id}`}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.25,
          p: 0,
          border: "none",
          borderBottom: isOther ? "none" : "2px dashed var(--theme-primary)",
          background: "none",
          font: "inherit",
          color: "var(--theme-text-primary)",
          cursor: disabled ? "default" : "pointer",
          "&:hover": { borderBottomColor: disabled ? undefined : "var(--theme-primary-light)" },
        }}
      >
        {isOther ? null : triggerLabel}
        <ArrowDropDown fontSize="small" sx={{ color: "var(--theme-text-secondary)" }} />
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={anchorEl !== null}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              minWidth: 220,
              bgcolor: "var(--theme-surface)",
              border: "1px solid var(--theme-border)",
              borderRadius: 1.5,
            },
          },
        }}
      >
        {candidates.map((option) =>
          renderOptionRow(
            option.id,
            option.label,
            !isAll && !isOther && selectedOptionIds.includes(option.id),
            optionHint(option),
          ),
        )}
        {allowMultiple ? renderOptionRow(ALL_OPTION_ID, t("clarification.allOption"), isAll) : null}
        {slot.allow_other !== false
          ? renderOptionRow(OTHER_OPTION_ID, t("clarification.otherOption"), isOther)
          : null}
      </Menu>
    </Box>
  );
}
